import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UilTimes, UilPlus } from '@iconscout/react-unicons';
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  query,
  where
} from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';
import SearchModal from '../../components/ModalSearch';

const VALID_CATEGORIES = ['general', 'trending-headlines', 'followed'];
const VALID_EXCHANGES = ['NASDAQ', 'NYSE'];

const CATEGORIES = [
  { id: 'general', name: 'Generelt' },
  { id: 'trending-headlines', name: 'Trending Headlines' },
  { id: 'followed', name: 'Aktier du følger' },
];

function FeedRightContent({ selectedCategories = ['general'], setSelectedCategories }) {
  const [followedStocks, setFollowedStocks] = useState([]);
  const [followedUsers, setFollowedUsers] = useState([]);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const db = getFirestore();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setFollowedStocks([]);
        setFollowedUsers([]);
        return;
      }
      try {
        // Fetch followed stocks
        const userStocksRef = collection(db, 'users', user.uid, 'followedStocks');
        const stockSnapshot = await getDocs(userStocksRef);
        const stocks = stockSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setFollowedStocks(stocks);

        // Fetch followed users
        const followingRef = collection(db, 'following');
        const userQuery = query(followingRef, where('followerId', '==', user.uid));
        const userSnapshot = await getDocs(userQuery);
        const users = userSnapshot.docs.map(doc => doc.data());
        setFollowedUsers(users);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [db, user]);

  useEffect(() => {
    const currentCategory = selectedCategories[0]?.toLowerCase();
    if (!currentCategory || !VALID_CATEGORIES.includes(currentCategory)) {
      setSelectedCategories(['general']);
    }
  }, [selectedCategories, setSelectedCategories]);

  const handleCategoryClick = (categoryId) => {
    if (VALID_CATEGORIES.includes(categoryId)) {
      setSelectedCategories([categoryId]);
    } else {
      setSelectedCategories(['general']);
    }
  };

  const handleStockSelect = async (stock) => {
    if (!user || !VALID_EXCHANGES.includes(stock?.companyInfo?.exchange)) return;
    try {
      const stockRef = doc(db, 'users', user.uid, 'followedStocks', stock.symbol);
      await setDoc(stockRef, {
        symbol: stock.symbol,
        name: stock.companyInfo?.name || '',
        exchange: stock.companyInfo?.exchange.toUpperCase()
      });
      setFollowedStocks((prev) => [...prev, { id: stock.symbol, ...stock.companyInfo }]);
      setSearchModalOpen(false);
    } catch (error) {
      console.error('Error following stock:', error);
    }
  };

  const handleUnfollowStock = async (id) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'followedStocks', id));
      setFollowedStocks((prev) => prev.filter((stock) => stock.id !== id));
    } catch (error) {
      console.error('Error unfollowing stock:', error);
    }
  };

  return (
    <div className="w-full mb-6 xl:mb-0 xl:w-72 xl:ml-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm xl:sticky xl:top-16">
        <div className="p-4">
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-4">
            Vælg kategorier
          </div>
          <ul className="grid grid-cols-1 gap-2">
            {CATEGORIES.map((category) => (
              <li key={category.id} className="flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="truncate">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      {category.name}
                    </span>
                  </div>
                  <button
                    onClick={() => handleCategoryClick(category.id)}
                    className={`ml-2 text-xs font-medium px-2.5 py-1 rounded-full ${
                      selectedCategories.includes(category.id)
                        ? 'bg-green-500/20 text-green-700'
                        : 'border border-gray-200 dark:border-gray-700/60 text-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {selectedCategories.includes(category.id) ? '✔' : 'Vælg'}
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-6">
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-4">
              Værdipapirer jeg følger
            </div>
            <ul className="space-y-2">
              {followedStocks.map((stock) => (
                <li key={stock.id} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{stock.symbol}  {stock.name}</span>
                  <button onClick={() => handleUnfollowStock(stock.id)} className="text-red-500">
                    <UilTimes size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <SearchModal
        id="search-modal"
        modalOpen={searchModalOpen}
        setModalOpen={setSearchModalOpen}
        onStockSelect={handleStockSelect}
        followedStocks={followedStocks.map(stock => stock.symbol)}
        VALID_EXCHANGES={VALID_EXCHANGES}
      />
    </div>
  );
}

FeedRightContent.propTypes = {
  selectedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedCategories: PropTypes.func.isRequired,
};

export default FeedRightContent;
